/**
 * The email's editor: built from the reply's blocks, reporting them back on every change, and the
 * ways information and blocks get into it.
 *
 *   - Clicking a chip adds it where the cursor is (or, before anyone has clicked into the email,
 *     at the end of its words), with spaces so it never runs into the next word.
 *   - Dragging a chip drops it where it is let go.
 *   - Neither works inside a legal note: those are sent word for word.
 *   - "Paragraph", "Part that changes" and "Legal note" add a block after the one being edited.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, type Editor, type JSONContent } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { DocumentBlock, ReplyCatalog } from '../api/api';
import { fromEditor, toEditor } from '../editor/convert';
import { FIELD_DRAG_TYPE, replyExtensions } from '../editor/extensions';

const LEGAL_NOTICE = 'Legal notes are sent word for word, so information can’t be added to them.';

/** The end of the email's last paragraph with words in it: where a chip goes before anyone clicks in. */
function defaultPosition(doc: ProseMirrorNode): number {
  let withWords: number | null = null;
  let anyParagraph: number | null = null;
  doc.forEach((node, offset) => {
    if (node.type.name !== 'paragraph') return;
    anyParagraph = offset + node.nodeSize - 1;
    if (node.childCount > 0) withWords = anyParagraph;
  });
  return withWords ?? anyParagraph ?? 1;
}

function areaOf(editor: Editor | null, catalog: ReplyCatalog): string | null {
  if (!editor) return 'the email';
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name === 'legalNote') return null;
    if (node.type.name === 'conditionSide') {
      if (node.attrs.side === 'otherwise') return '“Otherwise”';
      const flag = $from.node(depth - 1).attrs.flag;
      return `“If ${catalog.conditions.find(condition => condition.name === flag)?.label ?? flag}”`;
    }
  }
  return 'the email';
}

/** A drop in the middle of a word moves to the end of that word. */
function wordEnd(doc: ProseMirrorNode, position: number): number {
  const $position = doc.resolve(position);
  const end = $position.end();
  let at = position;
  const isWordChar = (from: number) =>
    from < end && /[\p{L}\p{N}'’]/u.test(doc.textBetween(from, from + 1));
  if (at === $position.start() || !isWordChar(at - 1)) return at;
  while (isWordChar(at)) at += 1;
  return at;
}

function chipWithSpaces(doc: ProseMirrorNode, position: number, name: string): JSONContent[] {
  const $position = doc.resolve(position);
  const before = $position.nodeBefore;
  const after = $position.nodeAfter;
  const spaceBefore = !!before && (!before.isText || !/\s$/.test(before.text ?? ''));
  const spaceAfter = !after || !after.isText || !/^[\s.,;:!?)’'”]/.test(after.text ?? '');
  return [
    ...(spaceBefore ? [{ type: 'text', text: ' ' }] : []),
    { type: 'fieldChip', attrs: { name } },
    ...(spaceAfter ? [{ type: 'text', text: ' ' }] : []),
  ];
}

export function useEmailEditor(input: {
  /** Changing it starts the editor afresh: another reply, or another version loaded. */
  resetKey: string;
  catalog: ReplyCatalog;
  initial: DocumentBlock[];
  onChange: (document: DocumentBlock[]) => void;
}) {
  const onChange = useRef(input.onChange);
  onChange.current = input.onChange;
  const touched = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const insertAt = useCallback(
    (editor: Editor, range: { from: number; to: number }, name: string) => {
      const $from = editor.state.doc.resolve(range.from);
      if ($from.parent.type.name === 'legalNote') {
        setNotice(LEGAL_NOTICE);
        return;
      }
      const target = $from.parent.inlineContent
        ? range
        : { from: defaultPosition(editor.state.doc), to: defaultPosition(editor.state.doc) };
      setNotice(null);
      editor
        .chain()
        .focus()
        .insertContentAt(target, chipWithSpaces(editor.state.doc, target.from, name))
        .run();
    },
    [],
  );

  const editor = useEditor(
    {
      extensions: replyExtensions(input.catalog),
      content: toEditor(input.initial),
      onUpdate: ({ editor: current }) => onChange.current(fromEditor(current.getJSON())),
      onFocus: () => {
        touched.current = true;
      },
      editorProps: {
        attributes: {
          'aria-label': 'The email',
          'class':
            'min-h-[220px] text-[15px] leading-[1.9] text-zinc-900 outline-none [&_p]:my-1.5',
        },
        handleDrop: (view, event) => {
          const name = event.dataTransfer?.getData(FIELD_DRAG_TYPE);
          if (!name) return false;
          event.preventDefault();
          setDragging(false);
          const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
          const current = editorRef.current;
          if (at && current) {
            touched.current = true;
            const position = wordEnd(view.state.doc, at.pos);
            insertAt(current, { from: position, to: position }, name);
          }
          return true;
        },
      },
    },
    [input.resetKey, input.catalog],
  );
  const editorRef = useRef<Editor | null>(editor);
  editorRef.current = editor;

  useEffect(() => {
    touched.current = false;
    setNotice(null);
  }, [input.resetKey]);

  const area = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      touched.current ? areaOf(current, input.catalog) : 'the email',
  });

  const insertField = useCallback(
    (name: string) => {
      if (!editor) return;
      const { from, to } = editor.state.selection;
      const position = touched.current
        ? { from, to }
        : { from: defaultPosition(editor.state.doc), to: defaultPosition(editor.state.doc) };
      insertAt(editor, position, name);
    },
    [editor, insertAt],
  );

  /** Adds a block after the one being edited, or at the end, and puts the cursor in it. */
  const addBlock = useCallback(
    (kind: 'paragraph' | 'condition' | 'legal') => {
      if (!editor) return;
      const { $from } = editor.state.selection;
      const position =
        touched.current && $from.depth >= 1 ? $from.after(1) : editor.state.doc.content.size;
      const content: JSONContent =
        kind === 'paragraph'
          ? { type: 'paragraph' }
          : kind === 'legal'
            ? { type: 'legalNote' }
            : {
                type: 'condition',
                attrs: { flag: input.catalog.conditions[0]?.name ?? 'intake_complete' },
                content: [
                  {
                    type: 'conditionSide',
                    attrs: { side: 'then' },
                    content: [{ type: 'paragraph' }],
                  },
                  {
                    type: 'conditionSide',
                    attrs: { side: 'otherwise' },
                    content: [{ type: 'paragraph' }],
                  },
                ],
              };
      touched.current = true;
      editor
        .chain()
        .insertContentAt(position, content)
        .setTextSelection(position + (kind === 'condition' ? 3 : 1))
        .focus()
        .run();
    },
    [editor, input.catalog.conditions],
  );

  const chipDragHandlers = useCallback(
    (name: string) => ({
      draggable: true,
      onDragStart: (event: React.DragEvent) => {
        event.dataTransfer.setData(FIELD_DRAG_TYPE, name);
        event.dataTransfer.effectAllowed = 'copy';
        setDragging(true);
      },
      onDragEnd: () => setDragging(false),
    }),
    [],
  );

  return {
    editor,
    /** Where a clicked chip goes, in words; null inside a legal note. */
    area,
    insertField,
    addBlock,
    chipDragHandlers,
    dragging,
    notice,
    dismissNotice: () => setNotice(null),
  };
}
